// React and Router imports
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

// Firebase imports
import { auth } from "../../firebase.js";

// Component imports
import { useContextMenu } from "../shared/ContextMenu";
import { useSidebarWidth, SidebarResizeHandle } from "../shared/SidebarResizeHandle";

// Styles imports
import "../../css/DocumentationHomeComponent.css";
import "../../css/Sidebar.css";
import "../../css/WalkthroughSidebar.css";

function WalkthroughSidebar({ onToggleSidebar }) {

        const [collapsed, setCollapsed] = useState(() => {
                return window.innerWidth > 600;
        });
        const [tutorialDetails, setTutorialDetails] = useState([])
        const [sharedTutorialDetails, setSharedTutorialDetails] = useState([])
        const [loading, setLoading] = useState(true)
        const [uid, setUid] = useState("")
        const [userEmail, setUserEmail] = useState("")
        const [hasTutorials, setHasTutorials] = useState(true)
        const [viewingSharedTutorials, setViewingSharedTutorials] = useState(false)
        const [hasSharedTutorials, setHasSharedTutorials] = useState(true)
        const [searchOptions, setSearchOptions] = useState([]);
        const [showSearch, setShowSearch] = useState(false);
        const [authResolved, setAuthResolved] = useState(false);
        const { width: sidebarWidth, isResizing, setIsResizing, applyWidth, resetWidth } = useSidebarWidth();
        const navigate = useNavigate()
        const location = useLocation()

        // Highlight the walkthrough currently being viewed. Names can
        // contain characters the router encodes, so compare against the
        // encoded form of the link as well.
        const isLinkActive = (linkPath) => {
                if (location.pathname === linkPath) return true;
                const encoded = linkPath
                        .split("/")
                        .map((segment, index) => (index < 2 ? segment : encodeURIComponent(segment)))
                        .join("/");
                return location.pathname === encoded;
        };

        // Create dynamic context menu options for walkthrough items
        const getSidebarItemContextOptions = (itemData) => {
            if (!itemData) return [];
            
            const { id, name, uid, isShared, sharedUrl } = itemData;
            
            // Construct correct URL based on item type
            let url;
            if (isShared && sharedUrl) {
                url = `https://bluebird-documentation.com/${sharedUrl}`;
            } else {
                url = `/walkthroughs/${name}/${uid}`;
            }
            
            const handleDeleteWalkthrough = async () => {
                const warningMessage = "Are you sure you want to delete this walkthrough?\n\n-This action cannot be undone.";
                const isConfirmed = window.confirm(warningMessage);
                if (!isConfirmed) {
                    return;
                }
                
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        alert("You must be logged in to delete walkthroughs");
                        return;
                    }

                    const token = await user.getIdToken();
                    const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/walkthrough/${id}/`, {
                        method: "DELETE",
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `${token}`,
                            'Auth-Token': `Bearer ${token}`,
                            'Requesting-User-Uid': user.uid
                        }
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || "Failed to delete walkthrough");
                    }

                    alert("Walkthrough deleted successfully!");
                    // Refresh the page
                    window.location.reload();
                } catch (error) {
                    console.error("Error deleting walkthrough:", error);
                    alert(`Error deleting walkthrough: ${error.message}`);
                }
            };
            
            // Only show delete option for user's own walkthroughs, not shared ones
            const options = [
                {
                    label: "Open",
                    icon: <i className="fas fa-arrow-right"></i>,
                    onClick: () => {
                        if (isShared && sharedUrl) {
                            window.location.href = url;
                        } else {
                            navigate(url);
                        }
                    }
                },
                {
                    label: "Open in New Tab",
                    icon: <i className="fas fa-external-link-alt"></i>,
                    url: url,
                    onClick: () => window.open(url, "_blank", "noopener,noreferrer")
                },
                {
                    label: "Open in New Window",
                    icon: <i className="fas fa-window-maximize"></i>,
                    onClick: () => window.open(url, "_blank", "width=1200,height=800,noopener,noreferrer")
                },
                {
                    label: "Copy Link",
                    icon: <i className="fas fa-copy"></i>,
                    onClick: () => {
                        const fullUrl = isShared && sharedUrl ? url : `${window.location.origin}${url}`;
                        navigator.clipboard.writeText(fullUrl);
                        alert("Link copied to clipboard!");
                    }
                }
            ];
            
            return options;
        };

        const { showContextMenu, ContextMenuComponent } = useContextMenu(getSidebarItemContextOptions);

        // Wrap showContextMenu to ensure proper event handling
        const handleSidebarItemContextMenu = (e, itemData) => {
            e.preventDefault();
            e.stopPropagation();
            showContextMenu(e.clientX, e.clientY, itemData);
        };

        useEffect(() => {
                window.addEventListener("resize", handleResize);
                return () => {
                        window.removeEventListener("resize", handleResize);
                };
        }, []);

        useEffect(() => {
                if (onToggleSidebar) {
                        onToggleSidebar(collapsed);
                }
        }, [collapsed, onToggleSidebar]);


        useEffect(() => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                        if (user) {
                                setUid(user.uid);
                                setUserEmail(user.email)
                        } else {
                                setUid(null);
                                setUserEmail(null)
                        }
                        setAuthResolved(true)
                });
                return () => { unsubscribe(); };
        }, []);

        useEffect(() => {
                const fetchTutorialDetails = async () => {
                        if (!uid) return;
                        try {
                                const user = auth.currentUser;
                                const token = user ? await user.getIdToken() : null;
                                if (!token) {
                                        console.error("User is not authenticated.");
                                        return;
                                }
                                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/walkthrough/${uid}/`, {
                                        method: "GET",
                                        headers: {
                                                'requesting-user-uid': uid,
                                                'requesting-user-email': userEmail,
                                                "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                        }
                                })
                                if (response.status === 404) {
                                        setHasTutorials(false)
                                        setLoading(false)
                                        return
                                }
                                const data = await response.json()
                                setLoading(false)
                                setTutorialDetails(data);
                                setHasTutorials(true)
                                prepareSearchOptions(data);
                        } catch (error) {
                                console.error("Error fetching page details: ", error)
                        }
                }
                fetchTutorialDetails()
        }, [uid])

        const toggleSidebar = () => {
                setCollapsed((prev) => {
                        const newState = !prev;
                        if (onToggleSidebar) {
                                onToggleSidebar(newState);
                        }
                        return newState;
                });
        };

        const prepareSearchOptions = (tutorials) => {
            const options = tutorials.map(tutorial => ({
                value: {
                    id: tutorial.id,
                    name: tutorial.tutorial_name,
                    uid: uid
                },
                label: `Walkthrough: ${tutorial.tutorial_name}`
            }));
            
            setSearchOptions(prevOptions => {
                // Filter out shared tutorials if they exist in the current options
                const filteredPrev = prevOptions.filter(option => 
                    option.value.uid !== uid
                );
                return [...filteredPrev, ...options];
            });
        };        

        const prepareSearchOptionsForShared = (tutorials) => {
            const options = tutorials.map(tutorial => ({
                value: {
                    id: tutorial.id,
                    name: tutorial.tutorial_name,
                    uid: tutorial.author_uid,
                    url: tutorial.tutorial_url
                },
                label: `Shared Walkthrough: ${tutorial.tutorial_name}`
            }));
            
            setSearchOptions(prevOptions => {
                // Filter out user's own tutorials if they exist in the current options
                const filteredPrev = prevOptions.filter(option => 
                    option.value.uid === uid
                );
                return [...filteredPrev, ...options];
            });
        };        

        const handleResize = () => {
                const shouldCollapse = window.innerWidth > 600;
                setCollapsed(shouldCollapse);
                if (onToggleSidebar) {
                        onToggleSidebar(shouldCollapse);
                }
        };

        const fetchSharedTutorials = async () => {
                if (!uid) return
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) {
                                console.error("User is not authenticated.");
                                return;
                        }
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/walkthrough/shared-walkthroughs/`, {
                                method: "GET",
                                headers: {
                                        'requesting-user-uid': uid,
                                        'requesting-user-email': userEmail,
                                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                }
                        })
                        if (response.status === 404) {
                                setHasSharedTutorials(false)
                                return
                        }
                        const data = await response.json()
                        const isEmpty = Array.isArray(data) ? data.length === 0 : Object.keys(data).length === 0
                        if (isEmpty) {
                                setHasSharedTutorials(false)
                                return
                        }
                        setHasSharedTutorials(true)
                        setSharedTutorialDetails(data)
                        prepareSearchOptionsForShared(data);
                } catch (error) {
                        console.error("Error fetching shared walkthroughs: ", error)
                }
        }

        const handleUsersIconClick = () => {
                setViewingSharedTutorials(true)
                fetchSharedTutorials()
        }

        const handleUserIconClick = () => {
                setViewingSharedTutorials(false)
        }

        // Keep the current walkthrough visible in the list when navigating
        useEffect(() => {
                const activeLink = document.querySelector(".sidebar .Sidebar__active-link");
                if (activeLink && typeof activeLink.scrollIntoView === "function") {
                        activeLink.scrollIntoView({ block: "nearest" });
                }
        }, [location.pathname, loading]);

        const handleSearchClick = () => { 
            setShowSearch(!showSearch);
        }
        
        const handleSearchSelect = (selected) => {
            if (selected) {
                const { name, uid, url } = selected.value;
                if (url) {
                    // For shared walkthroughs that have a specific URL
                    window.location.href = `https://bluebird-documentation.com/${url}`;
                } else {
                    // For user's own walkthroughs
                    navigate(`/walkthroughs/${name}/${uid}`);
                }
                setShowSearch(false);
            }
        }        

        const handleHelpClick = () => { navigate("/help") }
        // const toggleSidebar = () => { setCollapsed(!collapsed) }
        const handleNavigateClick = () => { navigate("/walkthroughs/create") }
        // const handleResize = () => { setCollapsed(window.innerWidth > 600) }

        if (!uid && authResolved) {
                return (
                    <div>
                        <button onClick={() => navigate("/login")} className="Sidebar__login-button">Login</button>
                    </div>
                )
        }

        return (
                <div className={`sidebar ${collapsed ? 'collapsed': ''} ${isResizing ? 'resizing' : ''}`} style={{ "--sidebar-width": `${sidebarWidth}px` }}>
                        <button onClick={toggleSidebar} className="toggle-button">
                                <i className={`fas ${collapsed ? 'fa-arrow-left' : 'fa-arrow-right'}`}></i>
                        </button>
                        <div className={`Sidebar__icon-container ${collapsed ? 'Sidebar__icon-container': 'Sidebar__hide-content'}`}>
                                <Link to="/">
                                        <i className="fas fa-home Sidebar__icons" title="Home"></i>
                                </Link>
                                <i id="user-icon" onClick={handleUserIconClick} className={`fa-solid fa-user Sidebar__icons ${!viewingSharedTutorials ? 'Sidebar__icon-active' : ''}`} title="View your walkthroughs."></i>
                                <i id="users-icon" onClick={handleUsersIconClick} className={`fa-solid fa-users Sidebar__icons ${viewingSharedTutorials ? 'Sidebar__icon-active' : ''}`} title="View walkthroughs shared with you."></i>
                        </div>
                        <div className={`Sidebar__search-dropdown ${!collapsed ? 'Sidebar__search-dropdown-collapsed' : ''}`}>
                            <Select
                                options={searchOptions}
                                onChange={handleSearchSelect}
                                placeholder="All Walkthroughs"
                                isClearable
                                className="Sidebar__search-select"
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                    control: (base) => ({
                                        ...base,
                                        backgroundColor: 'var(--bb-bg)',
                                        borderColor: 'var(--bb-text)',
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        backgroundColor: 'var(--bb-bg)',
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isFocused 
                                            ? 'var(--bb-border)' 
                                            : 'var(--bb-bg)',
                                        color: 'var(--bb-text)',
                                    }),
                                }}
                            />
                        </div>
                        {loading ? (
                                <div>Loading...</div>
                        ) : (
                                <nav className={`${collapsed ? '': 'Sidebar__hide-content'}`}>
                                        {hasTutorials && viewingSharedTutorials !== true ? (
                                                <ul className="Sidebar__ul">
                                                        {tutorialDetails.map((tutorial, index) => (
                                                                <li key={index}>
                                                                        <Link
                                                                            key={`${tutorial.tutorial_name}`}
                                                                            to={`/walkthroughs/${tutorial.tutorial_name}/${uid}`}
                                                                            title={tutorial.tutorial_name}
                                                                            className={isLinkActive(`/walkthroughs/${tutorial.tutorial_name}/${uid}`) ? 'Sidebar__active-link' : ''}
                                                                            onContextMenu={(e) => handleSidebarItemContextMenu(e, {
                                                                                id: tutorial.id,
                                                                                name: tutorial.tutorial_name,
                                                                                uid: uid,
                                                                                isShared: false
                                                                            })}
                                                                        >
                                                                            <i className="fa-solid fa-book-open Sidebar__page-icon"></i>
                                                                            {tutorial.tutorial_name}
                                                                        </Link>
                                                                </li>
                                                        ))}
                                                </ul>
                                        ) : hasSharedTutorials && viewingSharedTutorials ? (
                                                <ul className="Sidebar__ul">
                                                        {sharedTutorialDetails.map((item, index) => (
                                                                <li key={index}>
                                                                        <Link 
                                                                            key={`${item.tutorial_name}`} 
                                                                            to={`https://bluebird-documentation.com/${item.tutorial_url}`} 
                                                                            title={item.tutorial_name}
                                                                            onContextMenu={(e) => handleSidebarItemContextMenu(e, {
                                                                                id: item.id,
                                                                                name: item.tutorial_name,
                                                                                uid: item.author_uid,
                                                                                isShared: true,
                                                                                sharedUrl: item.tutorial_url
                                                                            })}
                                                                        >
                                                                            <i className="fa-solid fa-book-open Sidebar__page-icon"></i>
                                                                            {item.tutorial_name}
                                                                        </Link>
                                                                </li>
                                                        ))}
                                                </ul>
                                        ) : viewingSharedTutorials && hasSharedTutorials !== true ? (
                                                <p className="Sidebar__no-content" style={{ padding: "10px", fontSize: "16px" }}>No shared walkthroughs to show.</p>
                                        ): (
                                                <p className="Sidebar__no-content" style={{ padding: "10px", fontSize: "16px" }}>No walkthroughs to show.</p>
                                        )}
                                </nav>
                        )}
                        {collapsed && (
                                <>
                                        <button className="Sidebar__create-button" onClick={handleNavigateClick}>
                                                <i className="fas fa-plus Sidebar__plus-sign"></i>&nbsp; Create
                                        </button>
                                        <div className="Sidebar__bottom-icons">
                                                <i className="fa-solid fa-circle-question Sidebar__help" onClick={handleHelpClick} title="Help"></i>
                                        </div>
                                </>
                        )}
                        {ContextMenuComponent}
                        {collapsed && (
                                <SidebarResizeHandle
                                        width={sidebarWidth}
                                        applyWidth={applyWidth}
                                        resetWidth={resetWidth}
                                        setIsResizing={setIsResizing}
                                />
                        )}
                </div>
        )

}

export default WalkthroughSidebar